import React, { createContext, useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { RO, GB } from 'country-flag-icons/react/3x2';
import { TRANSLATIONS, type Language } from '@/i18n/translations';

interface LanguageItem {
  code: Language;
  name: string;
  flag: React.ComponentType<{ className?: string; title?: string }>;
}

export const languages: LanguageItem[] = [
  { code: 'ro', name: 'Română', flag: RO },
  { code: 'en', name: 'English', flag: GB },
];

interface LanguageContextValue {
  currentLanguage: Language;
  changeLanguage: (lang: Language) => void;
  t: (key: string) => string;
}

const LanguageContext = createContext<LanguageContextValue | undefined>(undefined);

const STORAGE_KEY = 'tls.language';

export function LanguageProvider({ children }: { children: React.ReactNode }) {
  const [currentLanguage, setCurrentLanguage] = useState<Language>(() => {
    if (typeof window === 'undefined') return 'ro';
    const stored = localStorage.getItem(STORAGE_KEY);
    return stored === 'en' || stored === 'ro' ? stored : 'ro';
  });

  useEffect(() => {
    document.documentElement.lang = currentLanguage;
    localStorage.setItem(STORAGE_KEY, currentLanguage);
  }, [currentLanguage]);

  const changeLanguage = useCallback((lang: Language) => {
    setCurrentLanguage(lang);
  }, []);

  const t = useCallback(
    (key: string) => {
      const dict = TRANSLATIONS[currentLanguage] || TRANSLATIONS.ro;
      return dict[key] || key;
    },
    [currentLanguage]
  );

  const value = useMemo(
    () => ({ currentLanguage, changeLanguage, t }),
    [currentLanguage, changeLanguage, t]
  );

  return <LanguageContext.Provider value={value}>{children}</LanguageContext.Provider>;
}

export function useLanguage() {
  const ctx = useContext(LanguageContext);
  if (!ctx) throw new Error('useLanguage must be used within LanguageProvider');
  return ctx;
}

export function useT() {
  return useLanguage().t;
}
